import { useCallback, useState } from 'react';
import { useErrorHandling } from './useErrorHandling';
import { ClientError } from '@cobra/fe-lib/client-error-handling/classes/ClientError';
import {
    NoProductInAutoConfigErrorResult,
    ProductNotInAvailableProductsErrorResult,
} from '@cobra/fe-lib/client-error-handling/classes/ErrorResult';
import { getStoredAvailableProducts } from '@cobra/services/shop-session-storage.service';

export const useVerifyProductId = () => {
    const [loading, setLoading] = useState(false);
    const [verifiedProductId, setVerifiedProductId] = useState<number | undefined>();
    const { error, setError, resetError } = useErrorHandling<
        NoProductInAutoConfigErrorResult | ProductNotInAvailableProductsErrorResult
    >();

    const verify = useCallback(async (productId: number | undefined) => {
        if (!productId) {
            throw new ClientError<NoProductInAutoConfigErrorResult>({
                title: 'NO_PRODUCT_IN_AUTO_CONFIG',
            });
        }

        const availableProducts = await getStoredAvailableProducts();

        if (!availableProducts?.includes(productId)) {
            throw new ClientError<ProductNotInAvailableProductsErrorResult>({
                title: 'PRODUCT_NOT_IN_AVAILABLE_PRODUCTS',
            });
        }

        return productId;
    }, []);

    const verifyProductId = useCallback(
        async (productId: number | undefined) => {
            resetError();
            setLoading(true);
            try {
                setVerifiedProductId(await verify(productId));
            } catch (e) {
                if (e instanceof ClientError) {
                    setError(e);
                }
            } finally {
                setLoading(false);
            }
        },
        [resetError, setError, verify]
    );

    return {
        verifyProductId,
        verifiedProductId,
        error,
        loading,
    };
};
